import type { AiCapability } from '@/shared/model/ai-enums'

export type GenerationMode =
  | 'text-to-image'
  | 'image-to-image'
  | 'text-to-video'
  | 'image-to-video'
  | 'video-generation'
  | 'digital-human'

export interface GenerationModeConfig {
  type: GenerationMode
  route: string
  icon: string
  name: string
  description: string
  title: string
  intro: string
  promptPlaceholder: string
  promptLabel: string
  model: string
  defaultModelId: string
  modelDescription: string
  cost: number
  estimate: string
  examples: string[]
  outputKind: 'image' | 'video'
}

export const generationModes: readonly GenerationModeConfig[] = [
  {
    type: 'text-to-image',
    route: '/app/create/text-to-image',
    icon: 'T',
    name: '文生图',
    description: '文字生成图片',
    title: 'AI 文生图',
    intro: '描述你想要的画面，AI 会将文字转化为视觉作品',
    promptPlaceholder: '描述主体、场景、光线、构图和风格……',
    promptLabel: '描述你的创意',
    model: 'Seedream 4.0',
    defaultModelId: 'seedream-4',
    modelDescription: '高质量 · 通用模型',
    cost: 12,
    estimate: '20–40 秒',
    examples: ['未来城市', '电影人像', '产品海报'],
    outputKind: 'image',
  },
  {
    type: 'image-to-image',
    route: '/app/create/image-to-image',
    icon: '◩',
    name: '图生图',
    description: '重绘与风格转换',
    title: 'AI 图生图',
    intro: '上传原图，通过提示词完成重绘、扩图与风格转换',
    promptPlaceholder: '描述希望保留的内容和需要改变的部分……',
    promptLabel: '描述你的创意',
    model: 'SeedEdit 3.0',
    defaultModelId: 'seededit-3',
    modelDescription: '精准编辑 · 高一致性',
    cost: 16,
    estimate: '30–60 秒',
    examples: ['电商换景', '风格迁移', '局部重绘'],
    outputKind: 'image',
  },
  {
    type: 'text-to-video',
    route: '/app/create/text-to-video',
    icon: '◈',
    name: '文生视频',
    description: '文字生成动态画面',
    title: 'AI 文生视频',
    intro: '描述画面与镜头语言，AI 会从文字直接生成连贯视频',
    promptPlaceholder: '描述主体、场景、动作、镜头运动、光线和节奏……',
    promptLabel: '描述你的视频创意',
    model: 'Veo 3 Fast',
    defaultModelId: 'veo-3-fast',
    modelDescription: '快速生成 · 原生音效',
    cost: 42,
    estimate: '2–5 分钟',
    examples: ['品牌短片', '电影分镜', '动态广告'],
    outputKind: 'video',
  },
  {
    type: 'image-to-video',
    route: '/app/create/image-to-video',
    icon: '▶',
    name: '图生视频',
    description: '静态画面动态化',
    title: 'AI 图生视频',
    intro: '从一张图片出发，设定镜头运动并生成连贯视频',
    promptPlaceholder: '描述主体动作、镜头运动、环境变化和节奏……',
    promptLabel: '动态描述',
    model: 'Kling 2.1 Pro',
    defaultModelId: 'kling-2-1-pro',
    modelDescription: '电影运镜 · 1080P',
    cost: 36,
    estimate: '2–5 分钟',
    examples: ['镜头推进', '人物转身', '云层流动'],
    outputKind: 'video',
  },
  {
    type: 'video-generation',
    route: '/app/create/video-generation',
    icon: '◉',
    name: '多模态视频生成',
    description: '图片、视频与音频参考创作',
    title: 'AI 多模态视频生成',
    intro: '先选择模型，再使用模型支持的图片、视频或音频参考生成新视频',
    promptPlaceholder: '描述想生成的画面、主体动作、镜头与节奏……',
    promptLabel: '描述你的视频创意',
    model: '请选择生成模型',
    defaultModelId: '',
    modelDescription: '按模型能力输入素材',
    cost: 0,
    estimate: '以实际任务为准',
    examples: [],
    outputKind: 'video',
  },
  {
    type: 'digital-human',
    route: '/app/create/digital-human',
    icon: '◎',
    name: '数字人',
    description: '口播与虚拟形象',
    title: 'AI 数字人',
    intro: '选择人物与音色，输入脚本即可生成自然口播视频',
    promptPlaceholder: '输入数字人口播脚本，可使用标点控制停顿与语气……',
    promptLabel: '口播脚本',
    model: 'Avatar Studio',
    defaultModelId: 'avatar-studio',
    modelDescription: '自然口型 · 多语言',
    cost: 48,
    estimate: '3–8 分钟',
    examples: ['产品介绍', '知识讲解', '新闻播报'],
    outputKind: 'video',
  },
]

const generationModeByCapability: Partial<
  Record<AiCapability, GenerationMode>
> = {
  TEXT_TO_IMAGE: 'text-to-image',
  IMAGE_TO_IMAGE: 'image-to-image',
  TEXT_TO_VIDEO: 'text-to-video',
  IMAGE_TO_VIDEO: 'image-to-video',
  VIDEO_GENERATION: 'video-generation',
  DIGITAL_HUMAN_VIDEO: 'digital-human',
}

const capabilityByGenerationMode: Record<GenerationMode, AiCapability> = {
  'text-to-image': 'TEXT_TO_IMAGE',
  'image-to-image': 'IMAGE_TO_IMAGE',
  'text-to-video': 'TEXT_TO_VIDEO',
  'image-to-video': 'IMAGE_TO_VIDEO',
  'video-generation': 'VIDEO_GENERATION',
  'digital-human': 'DIGITAL_HUMAN_VIDEO',
}

export const generationModeLabels: Record<GenerationMode, string> = {
  'text-to-image': '文生图',
  'image-to-image': '图生图',
  'text-to-video': '文生视频',
  'image-to-video': '图生视频',
  'video-generation': '多模态视频生成',
  'digital-human': '数字人',
}

export function getGenerationModeByCapability(capability: AiCapability) {
  return generationModeByCapability[capability]
}

export function getCapabilityByGenerationMode(mode: GenerationMode) {
  return capabilityByGenerationMode[mode]
}

export function getGenerationMode(type: GenerationMode) {
  const config = generationModes.find((item) => item.type === type)
  if (!config) throw new Error(`Unknown generation mode: ${type}`)
  return config
}
